import CallIcon from '@mui/icons-material/Call';
import {
  Avatar,
  Box,
  Breakpoint,
  Button,
  Container as MuiContainer,
  IconButton,
  Link,
  Stack,
  styled,
  Toolbar as MuiToolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { spacing, Theme } from '@mui/system';
import React, { createContext, PropsWithChildren, useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useGetFeatureFlagsQuery } from '../../../api/features';
import { AlertBanner } from '../../../app/components/alert-banner';
import { portalTypeSelector } from '../../../app/selectors/portal-type.selector';
import { COAST_VOICE_PHONE } from '../../../env';
import { gotoClientPortal } from '../../../store/actions/route';
import { ErrorType } from '../../../store/reducers/common';
import { Logo } from '../../../theme/coast/logo';
import { isEmbedded } from '../../../utils';
import { ErrorMessage } from '../error-message';
import { ProgressStepper } from '../progress-stepper';

const Container = styled(MuiContainer)(spacing);
const Toolbar = styled(MuiToolbar)(spacing);

const WizardBackContext = createContext<(() => void) | undefined>(undefined);

const useWizardBack = () => {
  return useContext(WizardBackContext);
};

type Props = PropsWithChildren<{
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtext?: React.ReactNode;
  subtextSpacing?: number;
  childrenSpacing?: number;
  features?: React.ReactNode;
  footer?: React.ReactNode;
  onBack?: () => void;
  error?: ErrorType | null;
  formWidth?: Breakpoint;
}>;

const ApplyToolbar: React.FC = () => {
  const { data: featureFlags } = useGetFeatureFlagsQuery();
  const theme = useTheme();
  const dispatch = useDispatch();
  const embedded = isEmbedded();

  const handleSignIn = useCallback(() => {
    dispatch(gotoClientPortal());
  }, [dispatch]);

  // extending mobile breakpoint for toolbar to accomodate testimonial panel
  const mobile = useMediaQuery('(max-width: 1268px)');

  const signInBtn = (
    <Button variant="outlined" size="large" color="primary" onClick={handleSignIn}>
      Sign In
    </Button>
  );

  return (
    <Box ml="auto" display="flex" alignItems="baseline">
      {featureFlags?.enableSupportNumber ? (
        <Stack direction="row" spacing={3} alignItems="center">
          {mobile ? (
            <IconButton
              href={`tel:${COAST_VOICE_PHONE}`}
              aria-label="Call Coast support"
              sx={{
                bgcolor: `transparent`,
                color: theme.palette.primary.main,
              }}
            >
              <CallIcon />
            </IconButton>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="inherit" color="textSecondary" display={{ xs: 'none', sm: 'initial' }}>
                Need help?
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: `transparent`,
                    color: theme.palette.primary.main,
                  }}
                >
                  <CallIcon />
                </Avatar>
                <Link href={`tel:${COAST_VOICE_PHONE}`}>{COAST_VOICE_PHONE}</Link>
              </Stack>
            </Stack>
          )}
          {!embedded && signInBtn}
        </Stack>
      ) : (
        !embedded && (
          <>
            <Typography variant="inherit" color="textSecondary" display={{ xs: 'none', sm: 'initial' }}>
              Have an account?
            </Typography>
            <Box ml={3}>{signInBtn}</Box>
          </>
        )
      )}
    </Box>
  );
};

const VerifyToolbar: React.FC = () => {
  const theme = useTheme();
  const mobile = useMediaQuery((theme: Theme) => {
    return theme.breakpoints.down('sm');
  });

  return (
    <Box ml="auto" display="flex" alignItems="baseline">
      <Stack direction="row" spacing={3} alignItems="center">
        {mobile ? (
          <IconButton
            href={`tel:${COAST_VOICE_PHONE}`}
            aria-label="Call Coast support"
            sx={{
              bgcolor: `transparent`,
              color: theme.palette.primary.main,
            }}
          >
            <CallIcon />
          </IconButton>
        ) : (
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="inherit" color="textSecondary" display={{ xs: 'none', sm: 'initial' }}>
              Need help?
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: `transparent`,
                  color: theme.palette.primary.main,
                }}
              >
                <CallIcon />
              </Avatar>
              <Link href={`tel:${COAST_VOICE_PHONE}`} aria-label="Call Coast support">
                ${COAST_VOICE_PHONE}
              </Link>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const WizardPage: React.FC<Props> = (props) => {
  const {
    icon,
    title,
    subtext,
    subtextSpacing = 2,
    childrenSpacing = 4,
    features,
    footer,
    onBack,
    error,
    children,
  } = props;

  const handleBack = useCallback(() => {
    onBack?.();
  }, [onBack]);

  const mobile = useMediaQuery((theme: Theme) => {
    return theme.breakpoints.down('sm');
  });

  const portalType = useSelector(portalTypeSelector);
  const { data: featureFlags } = useGetFeatureFlagsQuery();
  const showStepper = featureFlags?.fgEnableProgressIndicator && portalType === 'apply';

  return (
    <WizardBackContext.Provider value={onBack ? handleBack : undefined}>
      <Box display="flex">
        <Box flexGrow={1}>
          <AlertBanner />
          <Container py={2}>
            <Toolbar disableGutters>
              <Box component={Logo} mr={2} />
              {portalType == 'apply' ? <ApplyToolbar /> : <VerifyToolbar />}
            </Toolbar>
          </Container>

          <Container fixed pb={mobile && onBack ? 13 : 4}>
            {showStepper && <ProgressStepper />}

            {icon && (
              <Box display="flex" justifyContent="center" mb={5}>
                {icon}
              </Box>
            )}

            <Typography variant="h4" align="center" color="textPrimary">
              {title}
            </Typography>
            <Typography variant="body2" align="center" color="textSecondary" mt={subtextSpacing}>
              {subtext}
            </Typography>

            {features && <Box mt={4}>{features}</Box>}

            <Container maxWidth={props.formWidth ?? 'xs'} mt={childrenSpacing}>
              {error && (
                <Box mb={3}>
                  <ErrorMessage error={error} />
                </Box>
              )}

              {children}
            </Container>

            {footer ? (
              <Box display="flex" justifyContent="center" alignItems="center" mt={12}>
                {footer}
              </Box>
            ) : null}
          </Container>
        </Box>
        <Box id="pendo-guide" />
      </Box>
    </WizardBackContext.Provider>
  );
};

export { useWizardBack, WizardPage };
